import {
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  AnchoredPosition,
  calculateAnchoredPosition,
  calculateAnchoredWidth,
} from "components/designV2/anchoredPosition.logic";

const DEFAULT_GAP_PX = 4;
const DEFAULT_VIEWPORT_PADDING_PX = 8;

interface UseAnchoredPositionProps {
  readonly anchor?: "element" | "parent";
  readonly floatingRef: RefObject<HTMLElement>;
  readonly gap?: number;
  readonly isOpen: boolean;
  readonly minWidth?: number;
  readonly placement?: "start" | "end";
  readonly triggerRef: RefObject<HTMLElement>;
  readonly viewportPadding?: number;
  readonly widthMode?: "anchor" | "content";
}

interface MeasureAnchoredPositionProps {
  readonly anchor: "element" | "parent";
  readonly floatingElement: HTMLElement;
  readonly gap: number;
  readonly minWidth: number;
  readonly placement: "start" | "end";
  readonly triggerElement: HTMLElement;
  readonly viewportPadding: number;
  readonly widthMode: "anchor" | "content";
}

export const useAnchoredPosition = (
  props: UseAnchoredPositionProps,
): AnchoredPosition | null => {
  const [position, setPosition] = useState<AnchoredPosition | null>(null);
  const positionRef = useRef<AnchoredPosition | null>(null);
  const gap = props.gap ?? DEFAULT_GAP_PX;
  const viewportPadding = props.viewportPadding ?? DEFAULT_VIEWPORT_PADDING_PX;

  const updatePosition = useCallback((): void => {
    const triggerElement = props.triggerRef.current;
    const floatingElement = props.floatingRef.current;
    if (!triggerElement || !floatingElement) return;
    const nextPosition = measureAnchoredPosition({
      anchor: props.anchor ?? "element",
      floatingElement,
      gap,
      minWidth: props.minWidth ?? 0,
      placement: props.placement ?? "start",
      triggerElement,
      viewportPadding,
      widthMode: props.widthMode ?? "anchor",
    });
    if (isSamePosition(positionRef.current, nextPosition)) return;

    positionRef.current = nextPosition;
    setPosition(nextPosition);
  }, [
    gap,
    props.anchor,
    props.floatingRef,
    props.minWidth,
    props.placement,
    props.triggerRef,
    props.widthMode,
    viewportPadding,
  ]);

  useLayoutEffect(() => {
    if (!props.isOpen) return;
    updatePosition();
  });

  useEffect(() => {
    if (!props.isOpen) return;
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [props.isOpen, updatePosition]);

  return position;
};

function measureAnchoredPosition(
  props: MeasureAnchoredPositionProps,
): AnchoredPosition {
  const anchorElement =
    props.anchor === "parent"
      ? (props.triggerElement.parentElement ?? props.triggerElement)
      : props.triggerElement;
  const triggerBounds = anchorElement.getBoundingClientRect();
  const width = calculateAnchoredWidth({
    minWidth: props.minWidth,
    triggerWidth: triggerBounds.width,
    viewportPadding: props.viewportPadding,
    viewportWidth: window.innerWidth,
  });
  if (props.widthMode === "anchor") {
    props.floatingElement.style.width = `${width}px`;
  } else {
    props.floatingElement.style.removeProperty("width");
  }
  return calculateAnchoredPosition({
    floatingHeight: props.floatingElement.offsetHeight,
    floatingWidth:
      props.widthMode === "content"
        ? props.floatingElement.offsetWidth
        : undefined,
    gap: props.gap,
    minWidth: props.minWidth,
    placement: props.placement,
    triggerBounds,
    viewportHeight: window.innerHeight,
    viewportPadding: props.viewportPadding,
    viewportWidth: window.innerWidth,
  });
}

function isSamePosition(
  previous: AnchoredPosition | null,
  next: AnchoredPosition,
): boolean {
  return (
    previous?.left === next.left &&
    previous.top === next.top &&
    previous.width === next.width
  );
}
